import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import type { Reservation } from "../../hooks/api/useReservations";
import { useDateStore } from "../../stores/dateStore";

interface UpcomingReservationsSectionProps {
  reservations: Reservation[];
  onCancel: (reservation: Reservation) => void;
}

function groupReservationsByDate(
  reservations: Reservation[]
): Record<string, Reservation[]> {
  const grouped: Record<string, Reservation[]> = {};
  for (const reservation of reservations) {
    const { date } = reservation;
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(reservation);
  }
  return grouped;
}

export function UpcomingReservationsSection({
  reservations,
  onCancel,
}: UpcomingReservationsSectionProps) {
  const grouped = useMemo(
    () => groupReservationsByDate(reservations),
    [reservations]
  );
  const sortedDates = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  if (sortedDates.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-gray-500">
        <svg
          className="h-12 w-12 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
          />
        </svg>
        <p>今後の予約はありません</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedDates.map((date) => {
        const dateReservations = grouped[date];
        if (!dateReservations) {
          return null;
        }
        return (
          <div key={date}>
            <h4 className="mb-3 font-medium text-gray-900">
              {format(parseISO(date), "yyyy年M月d日（E）", {
                locale: ja,
              })}
            </h4>
            <div className="space-y-2">
              {dateReservations.map((reservation) => (
                <ReservationItem
                  key={`${reservation.date}-${reservation.seatId}`}
                  reservation={reservation}
                  onCancel={() => onCancel(reservation)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface ReservationItemProps {
  reservation: Reservation;
  onCancel: () => void;
}

function ReservationItem({ reservation, onCancel }: ReservationItemProps) {
  const navigate = useNavigate();
  const setSelectedDate = useDateStore((s) => s.setSelectedDate);
  const dateLabel = format(parseISO(reservation.date), "M月d日", {
    locale: ja,
  });
  const seatLabel = `座席 ${reservation.seat?.label ?? reservation.seatId}`;

  const handleViewDetails = () => {
    setSelectedDate(parseISO(reservation.date));
    navigate("/seats");
  };

  return (
    <div
      data-testid="upcoming-reservation-item"
      className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white p-4"
    >
      <div className="flex items-center gap-4">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-medium"
          aria-hidden="true"
        >
          {reservation.seatId}
        </div>
        <div>
          <div className="font-medium text-gray-900">{seatLabel}</div>
          <div className="text-sm text-gray-500">
            予約日時:{" "}
            {format(parseISO(reservation.createdAt), "yyyy/MM/dd HH:mm")}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleViewDetails}
          aria-label={`${dateLabel} ${seatLabel} の座席マップを表示`}
        >
          詳細を見る
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onCancel}
          aria-label={`${dateLabel} ${seatLabel} の予約をキャンセル`}
        >
          キャンセル
        </Button>
      </div>
    </div>
  );
}
